/**
 * Clamp Toggle helper
 *
 * Reusable behavior for truncating text blocks with a "show more/less" toggle.
 * Markup API:
 * - Wrapper: [data-clamp-toggle]
 * - Text: [data-clamp-text]
 * - Button: [data-clamp-button]
 * - Optional line count: data-clamp-lines="3" (on wrapper or text)
 *
 * @package Felix
 */

const WRAPPER_SELECTOR = '[data-clamp-toggle]'
const TEXT_SELECTOR = '[data-clamp-text]'
const BUTTON_SELECTOR = '[data-clamp-button]'

let hasBoundGlobalEvents = false
let isResizeScheduled = false

function setClampLines(textEl, wrapperEl) {
	const textLines = parseInt(textEl.dataset.clampLines || '', 10)
	const wrapperLines = parseInt(wrapperEl.dataset.clampLines || '', 10)
	const lines = Number.isFinite(textLines) && textLines > 0 ? textLines : wrapperLines

	if (!Number.isFinite(lines) || lines <= 0) {
		return
	}

	textEl.style.lineClamp = String(lines)
	textEl.style.webkitLineClamp = String(lines)
}

function applyState(wrapperEl) {
	const textEl = wrapperEl.querySelector(TEXT_SELECTOR)
	const buttonEl = wrapperEl.querySelector(BUTTON_SELECTOR)

	if (!textEl || !buttonEl) {
		return
	}

	setClampLines(textEl, wrapperEl)

	const wasExpanded = textEl.classList.contains('is-expanded')
	if (wasExpanded) {
		textEl.classList.remove('is-expanded')
	}

	const hasOverflow = textEl.scrollHeight > textEl.clientHeight + 1
	wrapperEl.classList.toggle('has-overflow', hasOverflow)
	buttonEl.hidden = !hasOverflow

	if (!hasOverflow) {
		buttonEl.setAttribute('aria-expanded', 'false')
		if (buttonEl.dataset.labelMore) {
			buttonEl.textContent = buttonEl.dataset.labelMore
		}
		return
	}

	if (wasExpanded) {
		textEl.classList.add('is-expanded')
		buttonEl.setAttribute('aria-expanded', 'true')
		if (buttonEl.dataset.labelLess) {
			buttonEl.textContent = buttonEl.dataset.labelLess
		}
	}
}

function handleToggleClick(event) {
	const buttonEl = event.target.closest(BUTTON_SELECTOR)
	if (!buttonEl) {
		return
	}

	const wrapperEl = buttonEl.closest(WRAPPER_SELECTOR)
	if (!wrapperEl) {
		return
	}

	const textEl = wrapperEl.querySelector(TEXT_SELECTOR)
	if (!textEl) {
		return
	}

	const isExpanded = textEl.classList.toggle('is-expanded')
	buttonEl.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')

	if (isExpanded && buttonEl.dataset.labelLess) {
		buttonEl.textContent = buttonEl.dataset.labelLess
	} else if (!isExpanded && buttonEl.dataset.labelMore) {
		buttonEl.textContent = buttonEl.dataset.labelMore
	}
}

function scheduleRecalculate() {
	if (isResizeScheduled) {
		return
	}

	isResizeScheduled = true
	window.requestAnimationFrame(() => {
		isResizeScheduled = false
		initClampToggles(document)
	})
}

export function initClampToggles(root = document) {
	if (!root || typeof root.querySelectorAll !== 'function') {
		return
	}

	const wrappers = root.querySelectorAll(WRAPPER_SELECTOR)
	wrappers.forEach((wrapperEl) => applyState(wrapperEl))
}

export function setupClampToggles(root = document) {
	initClampToggles(root)

	if (hasBoundGlobalEvents) {
		return
	}

	hasBoundGlobalEvents = true

	document.addEventListener('click', handleToggleClick)
	window.addEventListener('resize', scheduleRecalculate, { passive: true })

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => {
			initClampToggles(document)
		})
	}
}
